import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export interface SiteConfig {
	id: string;
	comingSoonEnabled: boolean;
	launchDate: string;
	maintenanceEnabled?: boolean;
	bannerEnabled?: boolean;
	bannerText?: string | null;
	dealsEnabled?: boolean;
	updatedAt: string;
}

export interface LaunchSubscriber {
	id: string;
	email: string;
	source: string;
	createdAt: string;
}

export interface LaunchSubscribers {
	count: number;
	items: LaunchSubscriber[];
}

export interface UpdateSiteConfigPayload {
	comingSoonEnabled?: boolean;
	launchDate?: string;
	maintenanceEnabled?: boolean;
	bannerEnabled?: boolean;
	bannerText?: string | null;
	dealsEnabled?: boolean;
}

// ─── Contenu éditorial de la home (mini-CMS) ────────────────────────────────
/**
 * Un volet du carrousel.
 *
 * `compose` : le site met en page accroche, titre, prix et bouton.
 * `image` : une bannière préparée en amont remplace toute la mise en page.
 * Absent sur les volets antérieurs, il vaut alors `compose`.
 */
export interface HeroSlide {
	mode?: "compose" | "image";
	eyebrow: string;
	title: string;
	ctaLabel: string;
	ctaHref: string;
	color: string;
	productSlug?: string | null;
	/** Bannière large (ordinateur) et bannière verticale (téléphone). */
	imageUrl?: string | null;
	imageMobileUrl?: string | null;
	/** Décrit l'image : en mode bannière, c'est le seul texte disponible. */
	imageAlt?: string | null;
}
export interface PromoCard {
	tone: "terra" | "forest";
	eyebrow: string;
	title: string;
	text: string;
	ctaLabel: string;
	href: string;
	big: string;
}
export interface TrustItem {
	icon: string;
	title: string;
	subtitle: string;
}
export interface HomeContent {
	id: string;
	heroSlides: HeroSlide[];
	promoCards: PromoCard[];
	trustItems: TrustItem[];
	seoTitle: string | null;
	seoDescription: string | null;
	seoKeywords: string[];
	seoOgImageMediaId: string | null;
	updatedAt: string;
}
export type UpdateHomeContentPayload = Partial<
	Omit<HomeContent, "id" | "updatedAt">
>;

// ─── Engagements commerciaux ────────────────────────────────────────────────

/**
 * Promesses affichées sur toute la vitrine.
 *
 * Ces valeurs étaient auparavant écrites en dur dans les traductions, dans les
 * contenus éditoriaux et dans une constante du serveur — d'où des délais de
 * rétractation contradictoires selon les pages. Le seuil de livraison offerte
 * pilote aussi le CALCUL réel des frais : ce qui est annoncé est ce qui est
 * appliqué au paiement.
 */
export interface SitePromises {
	// Absents de la lecture publique, présents dans la réponse d'écriture :
	// l'écran n'en a pas besoin, mais les typer requis ferait mentir la lecture.
	id?: string;
	returnDays: number;
	returnFree: boolean;
	freeShippingEnabled: boolean;
	freeShippingThreshold: number;
	etaMainMinHours: number;
	etaMainMaxHours: number;
	mainCities: string[];
	warrantyMonths: number;
	paymentMethods: string[];
	cashOnDelivery: boolean;
	updatedAt?: string;
}

export type UpdateSitePromisesPayload = Partial<
	Omit<SitePromises, "id" | "updatedAt">
>;

export interface PageRevision {
	id: string;
	action: string;
	authorId: string | null;
	createdAt: string;
}

export interface PageRevisions {
	items: PageRevision[];
	total: number;
}

export const siteApi = {
	getConfig: () =>
		unwrap<SiteConfig>(api.get<ApiEnvelope<SiteConfig>>("/admin/site/config")),

	updateConfig: (patch: UpdateSiteConfigPayload) =>
		unwrap<SiteConfig>(
			api.patch<ApiEnvelope<SiteConfig>>("/admin/site/config", patch),
		),

	subscribers: () =>
		unwrap<LaunchSubscribers>(
			api.get<ApiEnvelope<LaunchSubscribers>>("/admin/launch/subscribers"),
		),

	getHome: () =>
		unwrap<HomeContent>(api.get<ApiEnvelope<HomeContent>>("/admin/site/home")),

	updateHome: (patch: UpdateHomeContentPayload) =>
		unwrap<HomeContent>(
			api.put<ApiEnvelope<HomeContent>>("/admin/site/home", patch),
		),

	getPage: (key: string) =>
		unwrap<Record<string, Record<string, unknown>>>(
			api.get<ApiEnvelope<Record<string, Record<string, unknown>>>>(
				`/admin/site/page/${key}`,
			),
		),

	updatePage: (key: string, content: Record<string, Record<string, unknown>>) =>
		unwrap<Record<string, Record<string, unknown>>>(
			api.put<ApiEnvelope<Record<string, Record<string, unknown>>>>(
				`/admin/site/page/${key}`,
				{
					content,
				},
			),
		),

	// La lecture passe par la route publique : elle renvoie exactement ce que la
	// vitrine affiche, ce qui évite qu'un écart entre deux formes de réponse
	// laisse croire à un réglage enregistré alors qu'il ne l'est pas.
	getPromises: () =>
		unwrap<SitePromises>(api.get<ApiEnvelope<SitePromises>>("/site/promises")),

	updatePromises: (patch: UpdateSitePromisesPayload) =>
		unwrap<SitePromises>(
			api.put<ApiEnvelope<SitePromises>>("/admin/site/promises", patch),
		),

	/**
	 * Historique d'une page d'information.
	 *
	 * Les versions étaient archivées à chaque enregistrement depuis l'origine,
	 * mais aucun écran ne les montrait : on accumulait un historique que
	 * personne ne pouvait relire ni restaurer.
	 */
	pageRevisions: (key: string) =>
		unwrap<PageRevisions>(
			api.get<ApiEnvelope<PageRevisions>>(`/admin/site/page/${key}/revisions`),
		),

	pageRevision: (key: string, id: string) =>
		unwrap<Record<string, Record<string, unknown>>>(
			api.get<ApiEnvelope<Record<string, Record<string, unknown>>>>(
				`/admin/site/page/${key}/revisions/${id}`,
			),
		),

	restorePageRevision: (key: string, id: string) =>
		unwrap<Record<string, Record<string, unknown>>>(
			api.post<ApiEnvelope<Record<string, Record<string, unknown>>>>(
				`/admin/site/page/${key}/revisions/${id}/restore`,
			),
		),

	resetPage: (key: string) =>
		unwrap<Record<string, Record<string, unknown>>>(
			api.post<ApiEnvelope<Record<string, Record<string, unknown>>>>(
				`/admin/site/page/${key}/reset`,
			),
		),
};
